import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChatCircle, Archive, Tray } from "@phosphor-icons/react";
import { api, imgUrl, formatErr } from "@/lib/api";
import { FoundingHat } from "@/components/FoundingHat";
import PhishingWarningBanner from "@/components/PhishingWarningBanner";

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return d.toLocaleDateString();
}

export default function Messages() {
  const [box, setBox] = useState("inbox");
  const [convos, setConvos] = useState([]);
  const [counts, setCounts] = useState({ inbox: 0, archive: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/messages/conversations?box=${box}`);
      setConvos(data.conversations || []);
      setCounts(data.counts || { inbox: 0, archive: 0 });
    } catch (err) { toast.error(formatErr(err)); }
    finally { setLoading(false); }
  }, [box]);

  useEffect(() => {
    setLoading(true);
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  const TabBtn = ({ id, label, icon: Icon, count }) => (
    <button
      onClick={() => setBox(id)}
      data-testid={`messages-tab-${id}`}
      className={`inline-flex items-center gap-2 px-4 py-2 font-display font-bold uppercase text-xs tracking-widest border-b-2 transition-colors ${
        box === id
          ? "border-[#FF5F1F] text-white"
          : "border-transparent text-zinc-500 hover:text-zinc-300"
      }`}
    >
      <Icon size={14} weight="bold" />
      {label}
      <span className={`font-mono text-[10px] ${box === id ? "text-[#FF5F1F]" : "text-zinc-600"}`}>
        {count}
      </span>
    </button>
  );

  return (
    <div className="max-w-3xl mx-auto px-3 md:px-8 py-4 md:py-8">
      <div className="font-mono text-xs text-[#FF5F1F] tracking-widest uppercase">→ Messages</div>
      <h1 className="mt-2 font-display font-black uppercase text-3xl md:text-4xl tracking-tighter">Inbox</h1>

      <PhishingWarningBanner testId="messages-phishing-banner" />

      <div className="mt-6 border-b border-white/10 flex gap-1">
        <TabBtn id="inbox" label="Inbox" icon={Tray} count={counts.inbox} />
        <TabBtn id="archive" label="Archive" icon={Archive} count={counts.archive} />
      </div>

      {loading ? (
        <div className="py-24 text-center font-mono text-xs uppercase tracking-widest text-zinc-500">Loading…</div>
      ) : convos.length === 0 ? (
        <div className="py-16 text-center flex flex-col items-center" data-testid={`messages-empty-${box}`}>
          {box === "archive" ? (
            <>
              <Archive size={56} weight="bold" className="text-zinc-700" />
              <div className="mt-6 font-display font-black uppercase text-xl text-zinc-400">Nothing archived.</div>
              <p className="mt-2 text-sm text-zinc-500">Threads you archive from a conversation will show up here.</p>
            </>
          ) : (
            <>
              <img src="/graphics/empty-messages.png" alt="Bulldog with envelope" className="max-w-[280px] w-full opacity-80" />
              <div className="mt-6 font-display font-black uppercase text-xl text-zinc-400">No conversations yet.</div>
              <p className="mt-2 text-sm text-zinc-500">Message a seller from a listing to start a thread.</p>
            </>
          )}
        </div>
      ) : (
        <div className="mt-6 divide-y divide-white/5 border-y border-white/10">
          {convos.map((c) => (
            <Link
              key={c.id}
              to={`/messages/${c.id}`}
              data-testid={`convo-${c.id}`}
              className="flex items-center gap-4 p-4 hover:bg-white/5 transition-colors"
            >
              {c.listing?.front_image ? (
                <img src={imgUrl(c.listing.front_image, { w: 96 })} alt="" className="w-14 h-14 object-cover border border-white/10" loading="lazy" decoding="async" />
              ) : (
                <div className="w-14 h-14 bg-[#0A0A0A] border border-white/10 flex items-center justify-center">
                  <ChatCircle size={20} weight="bold" className="text-zinc-600" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-display font-bold text-sm truncate flex items-center gap-1.5">
                    @{c.other_user?.username || "?"}
                    <FoundingHat founding={c.other_user?.founding_member} size={11} />
                    {c.archived && <Archive size={11} weight="bold" className="text-zinc-500 shrink-0" />}
                  </div>
                  <div className="font-mono text-[10px] text-zinc-500 uppercase shrink-0">{fmtTime(c.last_message_at)}</div>
                </div>
                <div className="text-sm text-zinc-400 truncate mt-0.5">{c.last_message}</div>
                {c.listing?.title && <div className="font-mono text-[10px] text-zinc-500 uppercase truncate mt-0.5">Re: {c.listing.title}</div>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
